// Form nhập thông tin sinh một người — dùng chung cho các chủ đề Bói Đê
// (chủ đề bói đôi render 2 form). Controlled component: value/onChange.
import { UserRound, X } from "lucide-react";

import { lunarBirthDay, type BoiDePersonInput, type BoideProfile } from "@/lib/boide-api";
import { UnknownHourCta } from "@/components/doan-gio/unknown-hour-cta";

export type BirthFormValue = {
  ho_ten: string;
  ngay: number;
  thang: number;
  nam: number;
  /**
   * -1 = CHƯA CHỌN giờ sinh (không còn nghĩa "không rõ giờ").
   *
   * Giờ sinh nay là BẮT BUỘC với mọi lá số: nó quyết định trụ giờ Bát Tự và cung
   * Thân Tử Vi, nên lấp một giờ mặc định là bịa ra dữ kiện rồi luận trên đó. Gác
   * bằng `isBirthValueReadyForChart` trước khi cho gieo, và `toPersonInput` ném
   * lỗi nếu -1 lọt xuống tới payload.
   */
  gio: number;
  gioi_tinh: "nam" | "nu";
  is_lunar: boolean;
  /**
   * Sinh trong tháng âm NHUẬN — chỉ có nghĩa khi `is_lunar`. Optional để mọi chỗ
   * dựng giá trị cũ (thiếu khoá) vẫn đúng nghĩa "tháng thường".
   */
  is_leap_month?: boolean;
};

export function defaultBirthValue(): BirthFormValue {
  return { ho_ten: "", ngay: 1, thang: 1, nam: 2000, gio: -1, gioi_tinh: "nam", is_lunar: false };
}

export function toPersonInput(v: BirthFormValue): BoiDePersonInput {
  // Chốt chặn cuối: BE khai `gio: int = Field(ge=0, le=23)` nên -1 sẽ thành 422 khó
  // hiểu. Gác ở đây để lỗi nói đúng việc còn thiếu — nút gieo đã bị khoá từ trước
  // bởi `isBirthValueReadyForChart`, tới được đây là có nhánh nào quên gác.
  if (v.gio < 0) {
    throw new Error(`Chưa chọn giờ sinh cho ${v.ho_ten.trim() || "người này"}.`);
  }
  return {
    ho_ten: v.ho_ten.trim(),
    nam: v.nam,
    thang: v.thang,
    ngay: v.ngay,
    gio: v.gio,
    phut: 0,
    gioi_tinh: v.gioi_tinh,
    is_lunar: v.is_lunar,
    // Cờ nhuận còn sót từ lúc chọn âm lịch không được đi theo một ngày dương.
    is_leap_month: v.is_lunar && !!v.is_leap_month,
  };
}

export function isBirthValueComplete(v: BirthFormValue): boolean {
  return v.ho_ten.trim().length > 0;
}

/** Đã chọn giờ sinh chưa. `-1` là "chưa chọn", không phải "không rõ". */
export function isBirthHourSet(v: BirthFormValue): boolean {
  return v.gio >= 0;
}

/**
 * Đủ điều kiện LẬP LÁ SỐ: có tên và đã chọn giờ sinh.
 *
 * Tách khỏi `isBirthValueComplete` vì bói chỉ tay dùng hàm kia như "đã khai tên"
 * cho những thẻ không hỏi ngày sinh — gộp hai nghĩa vào một hàm sẽ khoá cứng
 * luồng chỉ-xem-tay.
 */
export function isBirthValueReadyForChart(v: BirthFormValue): boolean {
  return isBirthValueComplete(v) && isBirthHourSet(v);
}

/** Dựng giá trị form từ hồ sơ user (tự điền ô "Bạn" — vẫn chỉnh sửa được).
 * Trả null khi hồ sơ chưa khai ngày sinh — không đoán mò để user khỏi
 * submit nhầm ngày mặc định. */
export function birthValueFromProfile(profile: BoideProfile, hoTen: string): BirthFormValue | null {
  const g = String(profile.gender ?? "").toLowerCase();
  const gioi_tinh: "nam" | "nu" = g === "female" || g === "nu" || g === "nữ" ? "nu" : "nam";
  const rawGio = Number((profile.birth_time ?? "").slice(0, 2));
  const gio =
    profile.birth_time && Number.isInteger(rawGio) && rawGio >= 0 && rawGio <= 23 ? rawGio : -1;

  // `lunar_birth_date` của BE là CHUỖI hiển thị ("24/06/1997 ÂL") — phải bóc lấy
  // số ngày, còn tháng/năm đã là số sẵn.
  const lunarDay = lunarBirthDay(profile);
  if (
    profile.birth_calendar === "lunar" &&
    lunarDay &&
    profile.lunar_birth_month &&
    profile.lunar_birth_year
  ) {
    return {
      ho_ten: hoTen,
      ngay: lunarDay,
      thang: profile.lunar_birth_month,
      nam: profile.lunar_birth_year,
      gio,
      gioi_tinh,
      is_lunar: true,
      is_leap_month: !!profile.lunar_is_leap_month,
    };
  }
  if (profile.birth_date) {
    const [y, m, d] = profile.birth_date.slice(0, 10).split("-").map(Number);
    if (y && m && d) {
      return { ho_ten: hoTen, ngay: d, thang: m, nam: y, gio, gioi_tinh, is_lunar: false };
    }
  }
  return null;
}

const CHI_GIO = [
  "Tý",
  "Sửu",
  "Dần",
  "Mão",
  "Thìn",
  "Tỵ",
  "Ngọ",
  "Mùi",
  "Thân",
  "Dậu",
  "Tuất",
  "Hợi",
];

function hourLabel(h: number): string {
  const chiIdx = h === 23 ? 0 : Math.floor((h + 1) / 2) % 12;
  return `${h}h — giờ ${CHI_GIO[chiIdx]}`;
}

function Field({
  label,
  required = false,
  children,
}: {
  label: string;
  /** Hiện dấu * đỏ — cùng quy ước với các ô bắt buộc khác trong app. */
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs text-muted-foreground">
        {label}
        {required ? <span className="text-red-400"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

export function BirthInputForm({
  title,
  value,
  onChange,
  onRemove,
  hideBirthFields = false,
  showGender = false,
  footer,
  embedded = false,
}: {
  title?: string;
  value: BirthFormValue;
  onChange: (v: BirthFormValue) => void;
  /** Có mặt → hiện nút xoá người này (chủ đề cho thêm/bớt người). */
  onRemove?: () => void;
  /**
   * Chỉ hiện ô họ tên, ẩn toàn bộ phần ngày–giờ–giới tính–âm lịch.
   *
   * Dành cho những chủ đề mà thông tin sinh là TÙY CHỌN (bói chỉ tay). Ẩn thay vì
   * disable: một ô ngày sinh xám nhưng vẫn hiện 01/01/2000 khiến người ta tưởng con
   * số đó vẫn được gửi đi. Mặc định `false` nên các chủ đề Bói Đê không đổi gì.
   */
  hideBirthFields?: boolean;
  /**
   * Giữ lại ô GIỚI TÍNH khi đã ẩn phần ngày sinh.
   *
   * Bói chỉ tay cần nó ngay từ bước đầu dù ngày sinh để hỏi sau: tầng quét dùng
   * giới tính cho mục 1 (tiên thiên/hậu thiên) và cho quy ước nam tả nữ hữu khi
   * người xem không làm nghi thức chắp tay. Không khai thì mọi mục sau gắn nhãn
   * theo một bàn tay đoán.
   */
  showGender?: boolean;
  /** Chèn thêm nội dung cuối form (vd ô bật/tắt "chỉ xem tay"). */
  footer?: React.ReactNode;
  embedded?: boolean;
}) {
  const nowYear = new Date().getFullYear();
  const set = (patch: Partial<BirthFormValue>) => onChange({ ...value, ...patch });

  return (
    <div className={embedded ? "space-y-3" : "space-y-3 rounded-2xl border border-gold-soft/40 bg-card/50 p-4 backdrop-blur"}>
      {title || onRemove ? (
        <div className="flex items-center gap-2 border-b border-border/30 pb-2.5">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#E5B869]/12">
            <UserRound className="h-3.5 w-3.5 text-[#E5B869]" />
          </span>
          <span className="flex-1 truncate text-sm font-semibold text-gold-gradient">{title}</span>
          {onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              aria-label="Bỏ người này"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border/40 text-muted-foreground transition hover:border-red-400/50 hover:text-red-400"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}
      <Field label="Họ tên">
        <input
          value={value.ho_ten}
          onChange={(e) => set({ ho_ten: e.target.value })}
          className="input-mystic w-full"
          placeholder="Nguyễn Văn A"
        />
      </Field>
      {hideBirthFields ? null : (
        <Field label="Ngày sinh">
          <div className="flex gap-2">
            <select
              value={value.ngay}
              onChange={(e) => set({ ngay: Number(e.target.value) })}
              className="input-mystic min-w-[64px] flex-1"
              aria-label="Ngày"
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <select
              value={value.thang}
              onChange={(e) => set({ thang: Number(e.target.value) })}
              className="input-mystic min-w-[86px] flex-1"
              aria-label="Tháng"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  Tháng {m}
                </option>
              ))}
            </select>
            <select
              value={value.nam}
              onChange={(e) => set({ nam: Number(e.target.value) })}
              className="input-mystic min-w-[84px] flex-1"
              aria-label="Năm"
            >
              {Array.from({ length: nowYear - 1919 }, (_, i) => nowYear - i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </Field>
      )}
      {hideBirthFields ? null : (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Giờ sinh" required>
            <select
              value={value.gio}
              onChange={(e) => set({ gio: Number(e.target.value) })}
              className="input-mystic w-full"
              aria-required="true"
              aria-invalid={value.gio < 0}
            >
              <option value={-1} disabled>
                — Chọn giờ sinh —
              </option>
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {hourLabel(h)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Giới tính">
            <div className="grid grid-cols-2 gap-1 rounded-xl border border-input bg-background/40 p-1">
              {(["nam", "nu"] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => set({ gioi_tinh: g })}
                  className={`rounded-lg py-1.5 text-xs font-medium transition ${
                    value.gioi_tinh === g
                      ? "bg-gold-gradient text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {g === "nam" ? "Nam" : "Nữ"}
                </button>
              ))}
            </div>
          </Field>
        </div>
      )}
      {/* Ngoài <label> của ô giờ: <a> trong <label> khiến bấm vào là kích hoạt ô
          giờ chứ không mở trang đoán giờ. */}
      {hideBirthFields ? null : <UnknownHourCta />}
      {hideBirthFields && showGender ? (
        <Field label="Giới tính">
          <div className="grid grid-cols-2 gap-1 rounded-xl border border-input bg-background/40 p-1">
            {(["nam", "nu"] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => set({ gioi_tinh: g })}
                className={`rounded-lg py-1.5 text-xs font-medium transition ${
                  value.gioi_tinh === g
                    ? "bg-gold-gradient text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {g === "nam" ? "Nam" : "Nữ"}
              </button>
            ))}
          </div>
        </Field>
      ) : null}
      {hideBirthFields ? null : (
        <div className="grid grid-cols-2 gap-1 rounded-xl border border-input bg-background/40 p-1">
          {([false, true] as const).map((lunar) => (
            <button
              key={String(lunar)}
              type="button"
              // Đổi sang dương lịch thì bỏ luôn cờ nhuận để không gửi đi một cờ
              // còn sót lại từ lựa chọn trước.
              onClick={() =>
                set({ is_lunar: lunar, is_leap_month: lunar ? value.is_leap_month : false })
              }
              className={`rounded-lg py-1.5 text-xs font-medium transition ${
                value.is_lunar === lunar
                  ? "bg-gold-gradient text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {lunar ? "Âm lịch" : "Dương lịch"}
            </button>
          ))}
        </div>
      )}
      {!hideBirthFields && value.is_lunar ? (
        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={!!value.is_leap_month}
            onChange={(e) => set({ is_leap_month: e.target.checked })}
            className="size-4 accent-[#E5B869]"
          />
          Tháng nhuận
        </label>
      ) : null}
      {footer}
    </div>
  );
}
